import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { Input } from '@/shared/ui/inputs/Input';
import { Section } from '@/shared/ui/layout/Section';
import { usePinflLimit, computePinflLimitView } from '@/entities/pinfl';
import { formatUsd } from '@/shared/utils/currency';
import { env } from '@/shared/lib/env';
import { extractErrorMessage } from '@/shared/api/errors';
import { handleUppercaseChange } from '@/shared/lib/forms';
import { cn } from '@/shared/utils/cn';
import type { ParcelFormValues } from '@/features/parcel-form/modules/types';

type Props = {
  form: UseFormReturn<ParcelFormValues>;
};

// Owner spec mirrors admin: passport series is the 2-letter prefix + 7 digit
// serial, exactly 9 chars. We enforce maxLength so the field can't grow past
// it and uppercase on type so the zod regex (^[A-Z]{2}[0-9]{7}$) is always
// satisfied for valid input.
const PASSPORT_MAX = 9;

export const FeaturesParcelFormSectionPersonal = ({ form }: Props) => {
  const { t } = useTranslation('parcelForm');
  const { register, control, formState } = form;
  const errors = formState.errors;
  const pinfl = useWatch({ control, name: 'pinfl' }) ?? '';
  const limitQuery = usePinflLimit(pinfl);
  const passportField = register('passport_series');

  return (
    <Section title={t('sectionPersonal')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label={t('fields.lastName')}
          required
          error={errors.last_name?.message ? t(errors.last_name.message) : undefined}
          {...register('last_name')}
        />
        <Input
          label={t('fields.firstName')}
          required
          error={errors.first_name?.message ? t(errors.first_name.message) : undefined}
          {...register('first_name')}
        />
        <Input
          label={t('fields.middleName')}
          required
          error={errors.middle_name?.message ? t(errors.middle_name.message) : undefined}
          {...register('middle_name')}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label={t('fields.phone')}
          required
          inputMode="tel"
          placeholder={t('fields.phonePlaceholder')}
          error={errors.phone_number?.message ? t(errors.phone_number.message) : undefined}
          {...register('phone_number')}
        />
        <Input
          label={t('fields.passport')}
          required
          placeholder={t('fields.passportPlaceholder')}
          autoCapitalize="characters"
          maxLength={PASSPORT_MAX}
          error={errors.passport_series?.message ? t(errors.passport_series.message) : undefined}
          {...passportField}
          onChange={handleUppercaseChange(passportField.onChange)}
        />
      </div>
      <Input
        label={t('fields.pinfl')}
        required
        inputMode="numeric"
        maxLength={env.pinflLength}
        placeholder={t('fields.pinflPlaceholder', { length: env.pinflLength })}
        error={
          errors.pinfl?.message
            ? t(errors.pinfl.message, { length: env.pinflLength })
            : undefined
        }
        hint={renderPinflHint(limitQuery, t)}
        {...register('pinfl')}
      />
    </Section>
  );
};

type LimitQuery = ReturnType<typeof usePinflLimit>;

const renderPinflHint = (q: LimitQuery, t: TFunction<'parcelForm'>): React.ReactNode => {
  if (!q.isFetched && !q.isFetching) return null;
  if (q.isFetching) {
    return <span className="text-ink-muted">{t('pinfl.checking')}</span>;
  }
  if (q.error) {
    return <span className="text-danger">{extractErrorMessage(q.error, t('pinfl.loadError'))}</span>;
  }
  if (!q.data) return null;
  if (q.data.limit_applies === false) {
    return (
      <span className="font-medium text-ink-soft">{t('pinfl.noLimitLegal')}</span>
    );
  }
  const { used, ceiling, isOver } = computePinflLimitView(q.data);
  return (
    <span className={cn('font-medium', isOver ? 'text-danger' : 'text-brand-700')}>
      {t('pinfl.remaining', {
        used: formatUsd(used),
        total: formatUsd(ceiling),
      })}
    </span>
  );
};
